import { useEffect, useMemo, useState } from "react";
import { getTrustEngineV2Dashboard } from "../../../api/trustEngineV2Service";

function IntelligentTrustEnginePage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = async () => {
        try {
            setLoading(true);
            setError("");

            const result = await getTrustEngineV2Dashboard();
            setData(result);
        } catch (err) {
            console.error("Trust Engine V2 load failed", err);
            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Failed to load Trust Engine data."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const stats = useMemo(() => {
        if (!data) return [];

        return [
            ["Overall Trust", `${data.overallTrustScore ?? 0}%`, data.overallTrustScore],
            ["AI Risk", `${data.aiRiskScore ?? 0}%`, 100 - (data.aiRiskScore ?? 0)],
            ["Blockchain", `${data.blockchainIntegrity ?? 0}%`, data.blockchainIntegrity],
            ["IoT Reliability", `${data.ioTReliability ?? 0}%`, data.ioTReliability],
            ["GeoFence", `${data.geoFenceCompliance ?? 0}%`, data.geoFenceCompliance],
            ["Route", `${data.routeCompliance ?? 0}%`, data.routeCompliance],
            ["ETA Accuracy", `${data.etaAccuracy ?? 0}%`, data.etaAccuracy],
            ["Security", `${data.securityScore ?? 0}%`, data.securityScore],
            ["User Reputation", `${data.userReputation ?? 0}%`, data.userReputation],
            ["Anomaly", `${data.anomalyScore ?? 0}%`, 100 - (data.anomalyScore ?? 0)],
        ];
    }, [data]);

    if (loading) {
        return <div className="p-6 text-slate-600">Loading Intelligent Trust Engine...</div>;
    }

    if (error) {
        return (
            <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
                {error}
            </div>
        );
    }

    if (!data) {
        return (
            <div className="rounded-2xl bg-white border border-slate-200 shadow p-8 text-center">
                Trust Engine data not available.
            </div>
        );
    }

    return (
        <div className="w-full">
            <div className="mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900">
                        Intelligent Trust Engine v2
                    </h1>
                    <p className="text-slate-600 mt-2">
                        Adaptive trust evaluation based on AI risk, IoT reliability,
                        blockchain integrity, geofence compliance, route behavior,
                        ETA accuracy and security events.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={load}
                    className="rounded-xl bg-slate-800 px-5 py-3 text-white font-semibold hover:bg-slate-900 transition"
                >
                    Refresh Trust Engine
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-5 mb-6">
                {stats.map(([title, value, score]) => (
                    <TrustCard
                        key={title}
                        title={title}
                        value={value}
                        score={Number(score ?? 0)}
                    />
                ))}
            </div>

            <div className="bg-white rounded-2xl shadow border border-slate-200 p-6 mb-6">
                <h2 className="text-2xl font-bold mb-5">Trust Decision</h2>

                <div className={`rounded-2xl border p-6 ${getTrustPanelClass(data.overallTrustScore)}`}>
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                            <p className="text-sm uppercase tracking-wide font-semibold opacity-80">
                                Current Trust Level
                            </p>

                            <h3 className="text-4xl font-black mt-2">
                                {data.trustLevel || "Unknown"}
                            </h3>
                        </div>

                        <div className="text-5xl font-black">
                            {data.overallTrustScore ?? 0}%
                        </div>
                    </div>

                    <div className="mt-6 grid md:grid-cols-2 gap-4">
                        <InfoBox label="Recommendation" value={data.recommendation || "�"} />
                        <InfoBox label="Decision" value={data.decision || "�"} />
                    </div>

                    <ProgressBar score={Number(data.overallTrustScore ?? 0)} />
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <Panel title="Trust Factors">
                    {data.factors?.length > 0 ? (
                        <div className="space-y-3">
                            {data.factors.map((factor, index) => (
                                <FactorRow key={index} factor={factor} />
                            ))}
                        </div>
                    ) : (
                        <EmptyText text="No trust factors found." />
                    )}
                </Panel>

                <Panel title="Trust Risks">
                    {data.risks?.length > 0 ? (
                        <div className="space-y-3">
                            {data.risks.map((risk, index) => (
                                <RiskRow key={index} risk={risk} />
                            ))}
                        </div>
                    ) : (
                        <EmptyText text="No risks detected." />
                    )}
                </Panel>
            </div>
        </div>
    );
}

function TrustCard({ title, value, score }) {
    const color = getScoreColor(score);

    return (
        <div className="bg-white rounded-2xl shadow border border-slate-200 p-6 hover:shadow-xl transition">
            <p className="text-slate-500">{title}</p>

            <div className={`inline-flex mt-3 px-3 py-1 rounded-xl font-black text-2xl ${color.badge}`}>
                {value}
            </div>

            <div className="mt-4 h-3 w-full rounded-full bg-slate-200 overflow-hidden">
                <div
                    className={`h-full ${color.bar}`}
                    style={{ width: `${Math.min(Math.max(score, 0), 100)}%` }}
                />
            </div>
        </div>
    );
}

function FactorRow({ factor }) {
    const score = Number(factor.score ?? 0);
    const color = getScoreColor(score);

    return (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <h3 className="font-bold text-slate-900">{factor.name}</h3>
                    <p className="text-sm text-slate-500 mt-1">{factor.status}</p>
                </div>

                <span className={`rounded-xl px-3 py-1 font-black ${color.badge}`}>
                    {score}%
                </span>
            </div>

            <ProgressBar score={score} />
        </div>
    );
}

function RiskRow({ risk }) {
    return (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
            <h3 className="font-bold text-red-800">{risk.area}</h3>
            <p className="text-red-700 mt-1">{risk.risk}</p>
            <p className="text-sm text-red-600 mt-2">
                Action: {risk.action}
            </p>
        </div>
    );
}

function Panel({ title, children }) {
    return (
        <div className="bg-white rounded-2xl shadow border border-slate-200 p-6">
            <h2 className="text-2xl font-bold mb-5">{title}</h2>
            {children}
        </div>
    );
}

function InfoBox({ label, value }) {
    return (
        <div className="rounded-xl bg-white/70 border border-white/60 p-4">
            <p className="text-xs uppercase tracking-wide font-semibold opacity-70">
                {label}
            </p>
            <p className="font-bold mt-1">{value}</p>
        </div>
    );
}

function ProgressBar({ score }) {
    const color = getScoreColor(score);

    return (
        <div className="mt-4 h-3 w-full rounded-full bg-white/60 overflow-hidden">
            <div
                className={`h-full ${color.bar}`}
                style={{ width: `${Math.min(Math.max(score, 0), 100)}%` }}
            />
        </div>
    );
}

function EmptyText({ text }) {
    return <p className="text-slate-500">{text}</p>;
}

function getScoreColor(score) {
    if (score >= 85) {
        return {
            badge: "bg-green-100 text-green-700",
            bar: "bg-green-500",
        };
    }

    if (score >= 70) {
        return {
            badge: "bg-yellow-100 text-yellow-700",
            bar: "bg-yellow-500",
        };
    }

    return {
        badge: "bg-red-100 text-red-700",
        bar: "bg-red-500",
    };
}

function getTrustPanelClass(score) {
    const s = Number(score ?? 0);

    if (s >= 85) return "bg-green-50 border-green-200 text-green-800";
    if (s >= 70) return "bg-yellow-50 border-yellow-200 text-yellow-800";
    return "bg-red-50 border-red-200 text-red-800";
}

export default IntelligentTrustEnginePage;